/**
 * TeachBud Theme Picker
 *
 * Shared theme picker component used by all three generators.
 * Shows a grid of colored swatches the teacher can click to select a theme.
 * Theme changes affect visual appearance only — instructional content is unchanged.
 */

import React, { useState } from 'react';
import { CheckCircle2, ChevronDown, Palette, Star, RotateCcw, Layers } from 'lucide-react';
import { THEME_LIST, type TeachBudThemeId, type TeachBudTheme } from './theme-engine';

interface ThemePickerProps {
  value: TeachBudThemeId;
  onChange: (id: TeachBudThemeId) => void;
  /** If true, shows as a compact inline row (for toolbars). Default: full grid. */
  compact?: boolean;
  /** Label shown on the trigger button. */
  label?: string;
  /** Resource type — used to label save-default actions. */
  resourceType?: 'lesson' | 'activity' | 'presentation';
  /** Called when teacher clicks "Save as my default". */
  onSaveDefault?: () => void;
  /** Called when teacher clicks "Restore TeachBud default". Returns 'modern'. */
  onRestoreDefault?: () => void;
  /** Called when teacher clicks "Apply to all three linked resources". */
  onApplyToAll?: () => void;
}

function ThemeSwatch({ theme, selected, onClick }: {
  theme: TeachBudTheme;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      onClick={onClick}
      title={theme.name}
      className={`group relative flex flex-col items-center gap-1 p-2 rounded-xl border-2 transition-all hover:scale-105 ${
        selected
          ? 'border-primary shadow-md shadow-primary/20 scale-105'
          : 'border-transparent hover:border-slate-200'
      }`}
    >
      <div
        className="w-10 h-7 rounded-lg shadow-sm flex-shrink-0"
        style={{
          background: `linear-gradient(135deg, ${theme.swatch[0]} 0%, ${theme.swatch[1]} 100%)`,
        }}
      />
      <span className={`text-[9px] font-semibold leading-tight text-center w-12 truncate ${selected ? 'text-primary' : 'text-slate-500'}`}>
        {theme.name.split(' / ')[0]}
      </span>
      {selected && (
        <CheckCircle2 size={12} className="absolute top-1 right-1 text-primary fill-white" />
      )}
    </button>
  );
}

const RESOURCE_LABEL: Record<string, string> = {
  lesson: 'Lesson Theme',
  activity: 'Activity Sheet Theme',
  presentation: 'Presentation Theme',
};

export function ThemePicker({
  value, onChange, compact = false, label = 'Theme',
  resourceType, onSaveDefault, onRestoreDefault, onApplyToAll,
}: ThemePickerProps) {
  const [open, setOpen] = useState(false);
  const [savedFeedback, setSavedFeedback] = useState(false);
  const current = THEME_LIST.find(t => t.id === value) ?? THEME_LIST[1];

  function handleSaveDefault() {
    onSaveDefault?.();
    setSavedFeedback(true);
    setTimeout(() => setSavedFeedback(false), 2000);
  }

  if (compact) {
    return (
      <div className="relative">
        <button
          onClick={() => setOpen(v => !v)}
          className="flex items-center gap-2 px-3 py-2 rounded-xl border border-slate-200 bg-white hover:bg-slate-50 text-sm font-medium text-slate-700 transition-colors"
        >
          <div
            className="w-4 h-4 rounded-full flex-shrink-0 shadow-sm"
            style={{
              background: `linear-gradient(135deg, ${current.swatch[0]} 50%, ${current.swatch[1]} 100%)`,
            }}
          />
          <span>{current.name}</span>
          <ChevronDown size={13} className={`text-slate-400 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>

        {open && (
          <>
            <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} />
            <div className="absolute left-0 top-full mt-1 z-40 bg-white border border-slate-200 rounded-2xl shadow-xl p-3 w-80">
              <p className="text-xs font-semibold text-slate-400 mb-2 px-1">Choose a visual theme</p>
              <div className="grid grid-cols-6 gap-1">
                {THEME_LIST.map(theme => (
                  <ThemeSwatch
                    key={theme.id}
                    theme={theme}
                    selected={theme.id === value}
                    onClick={() => { onChange(theme.id); setOpen(false); }}
                  />
                ))}
              </div>

              {/* Action row */}
              {(onSaveDefault || onRestoreDefault || onApplyToAll) && (
                <div className="mt-3 pt-3 border-t border-slate-100 flex flex-col gap-1">
                  {onSaveDefault && (
                    <button
                      onClick={() => { handleSaveDefault(); setOpen(false); }}
                      className="flex items-center gap-2 text-xs text-slate-600 hover:text-primary hover:bg-primary/5 px-2 py-1.5 rounded-lg transition-colors text-left"
                    >
                      <Star size={11} className="text-amber-400 flex-shrink-0" />
                      {savedFeedback ? 'Saved!' : `Save as my default ${resourceType ? RESOURCE_LABEL[resourceType] : 'theme'}`}
                    </button>
                  )}
                  {onApplyToAll && (
                    <button
                      onClick={() => { onApplyToAll(); setOpen(false); }}
                      className="flex items-center gap-2 text-xs text-slate-600 hover:text-primary hover:bg-primary/5 px-2 py-1.5 rounded-lg transition-colors text-left"
                    >
                      <Layers size={11} className="text-slate-400 flex-shrink-0" />
                      Apply to all three linked resources
                    </button>
                  )}
                  {onRestoreDefault && (
                    <button
                      onClick={() => { onRestoreDefault(); setOpen(false); }}
                      className="flex items-center gap-2 text-xs text-slate-500 hover:text-slate-700 hover:bg-slate-50 px-2 py-1.5 rounded-lg transition-colors text-left"
                    >
                      <RotateCcw size={11} className="text-slate-400 flex-shrink-0" />
                      Restore TeachBud default
                    </button>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  }

  // Full grid (used in config screens)
  return (
    <div>
      <div className="flex items-center gap-2 mb-3">
        <Palette size={14} className="text-slate-400" />
        <span className="text-sm font-semibold text-slate-700">{label}</span>
      </div>
      <div className="grid grid-cols-6 gap-1.5 sm:grid-cols-6">
        {THEME_LIST.map(theme => (
          <ThemeSwatch
            key={theme.id}
            theme={theme}
            selected={theme.id === value}
            onClick={() => onChange(theme.id)}
          />
        ))}
      </div>
      <p className="mt-2 text-xs text-slate-400">Theme changes appearance only — instructional content stays the same.</p>
      {(onSaveDefault || onRestoreDefault || onApplyToAll) && (
        <div className="flex flex-wrap gap-3 mt-2">
          {onSaveDefault && (
            <button onClick={handleSaveDefault} className="flex items-center gap-1.5 text-xs text-primary hover:underline font-medium">
              <Star size={11} className="text-amber-400" />
              {savedFeedback ? 'Saved!' : `Save as my default${resourceType ? ` ${RESOURCE_LABEL[resourceType]}` : ''}`}
            </button>
          )}
          {onApplyToAll && (
            <button onClick={onApplyToAll} className="flex items-center gap-1.5 text-xs text-slate-500 hover:text-slate-700 hover:underline">
              <Layers size={11} /> Apply to all three
            </button>
          )}
          {onRestoreDefault && (
            <button onClick={onRestoreDefault} className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-600 hover:underline">
              <RotateCcw size={11} /> Restore default
            </button>
          )}
        </div>
      )}
    </div>
  );
}
